import {
  CompanyWidgetEvent,
  CompanyWidgetProps,
  GenerateWidgetConfig,
  ProductWidgetEvent,
  ProductWidgetProps,
  WidgetResponsePaths,
  WidgetResponsePromises,
  WidgetStyles,
} from "./types";
import {
  getCompanyFilePath,
  getCompanyUrl,
  getProductFilePath,
  getProductUrl,
} from "./path";
import { queryCompanyLatestResult, queryProductLatestResult } from "./query";

import { SupabaseClient } from "@supabase/supabase-js";
import { default as companyTemplates } from "./templates/company";
import { default as productTemplates } from "./templates/product";
import { saveToS3 } from "./file";

import type { BadgeAssets, BadgeMetals, CompanyBadgeTemplates, ProductBadgeTemplates } from "./types"

const sizesToGenerate: Array<WidgetStyles> = ["sm", "md", "lg"];

export const generateProductWidget = async (
  event: ProductWidgetEvent,
  client: SupabaseClient,
  { bucket }: GenerateWidgetConfig,
  isVerified?: boolean
) => {
  console.log(`Generating product widget for ${event.cid}/${event.pid}`);

  const { data, error } = await queryProductLatestResult(event, client);
  if (error) {
    console.error(error);
    throw new Error("Error fetching data.");
  } else if (!data || !data.survey_response) {
    throw new Error("No data found.");
  }

  const { name: title } = data.survey_response.company;
  const score = data.survey_response.score;

  const href = getProductUrl(event.cid, event.pid);
  const paths: WidgetResponsePaths = {};
  const promises: WidgetResponsePromises = [];
  const badge = getBadgeFromScore(score);
  const isVerifiedBadge: keyof BadgeAssets = isVerified ? 'v' : 'u';

	if (!badge) {
    throw new Error("Error - Product Sustainability ID did not qualify for a badge");
  }

  sizesToGenerate.forEach((size: keyof ProductBadgeTemplates) => {
    const props: ProductWidgetProps = {
      title,
      href,
      svg: productTemplates.assets[isVerifiedBadge][badge][size],
    };

    const widget = productTemplates.templates[size](props);
    const s3Path = getProductFilePath(event.cid, event.pid, size);
    paths[size] = s3Path;
    promises.push(saveToS3(bucket, s3Path, widget));
  });

  const saved = await Promise.allSettled(promises);

  if (saved.some((p) => p.status === "rejected")) {
    throw new Error("Error saving to S3.");
  }

  return {
    statusCode: 200,
    body: "Product Widget saved successfully.",
    paths,
  };
};

export const generateCompanyWidget = async (
  event: CompanyWidgetEvent,
  client: SupabaseClient,
  { bucket }: GenerateWidgetConfig,
  isVerified?: boolean
) => {
  console.log(`Generating company widget for ${event.cid}`);

  const { data, error } = await queryCompanyLatestResult(event, client);
  if (error) {
    console.error(error);
    throw new Error("Error fetching data.");
  } else if (!data || !data.survey_response) {
    throw new Error("No data found.");
  }

  const { name: title } = data.survey_response.company;
  const score = data.survey_response.score;

  const href = getCompanyUrl(event.cid);
  const paths: WidgetResponsePaths = {};
  const promises: WidgetResponsePromises = [];
  const badge = getBadgeFromScore(score);
  const isVerifiedBadge: keyof BadgeAssets = isVerified ? 'v' : 'u';

  if (!badge) {
    throw new Error("Error - Company Sustainability ID did not qualify for a badge");
  }

  sizesToGenerate.forEach((size: keyof CompanyBadgeTemplates) => {
    const props: CompanyWidgetProps = {
      title,
      href,
      svg: companyTemplates.assets[isVerifiedBadge][badge][size],
    };
    const widget = companyTemplates.templates[size](props);
    const s3Path = getCompanyFilePath(event.cid, size);
    paths[size] = s3Path;
    promises.push(saveToS3(bucket, s3Path, widget));
  });

  const saved = await Promise.allSettled(promises);

  if (saved.some((p) => p.status === "rejected")) {
    throw new Error("Error saving to S3.");
  }

  return {
    statusCode: 200,
    body: "Company Widget saved successfully.",
    paths,
  };
};

export const getBadgeFromScore = (score: number): keyof BadgeMetals | null => {
  if (score >= 70) return 'p';
  else if (score >= 55) return 'g';
  else if (score >= 40) return 's';

  return null;
}
